import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { createSnapModifier, restrictToParentElement } from '@dnd-kit/modifiers';
import { ConfiguratorDrawer } from 'components/Pages/Configurator/ConfiguratorDrawer';
import { ConfiguratorDrawerSizesForm } from 'components/Pages/Configurator/ConfiguratorDrawerSizesForm';
import { ConfiguratorHeader } from 'components/Pages/Configurator/ConfiguratorHeader';
import { ConfiguratorProducts } from 'components/Pages/Configurator/ConfiguratorProducts';
import { DRAWER_DROP_AREA_ID } from 'helpers/configurator/configuratorConstants';
import { getIsProductHaveCollision } from 'helpers/configurator/configuratorHelpers';
import { initServerSideProps } from 'helpers/InitServerSideProps';
import { disableScroll, enableScroll } from 'helpers/scrollHelper';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { useMemo } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { ConfiguratorCoordinatesType } from 'types/configurator';
import { NextPageExtended } from 'types/global';

const Configurator: NextPageExtended = () => {
    const { updateConfiguratorProduct, productsInDrawer, actualCellWidthInPixels } = usePersistStore();
    const getDraggedProduct = (draggedProductId: string) => {
        return productsInDrawer.find((product) => product.id === draggedProductId);
    };
    const snapToGrid = useMemo(() => createSnapModifier(actualCellWidthInPixels), [actualCellWidthInPixels]);

    const onDragEndHandler = ({ active, over, delta }: DragEndEvent) => {
        enableScroll();
        const draggedProduct = getDraggedProduct(active.id.toString());

        if (over?.id === DRAWER_DROP_AREA_ID && draggedProduct) {
            const newProductCoordinates: ConfiguratorCoordinatesType = {
                x: draggedProduct.coordinates.x + Math.round(delta.x / actualCellWidthInPixels),
                y: draggedProduct.coordinates.y + Math.round(delta.y / actualCellWidthInPixels),
            };

            if (
                !getIsProductHaveCollision({ ...draggedProduct, coordinates: newProductCoordinates }, productsInDrawer)
            ) {
                updateConfiguratorProduct(
                    {
                        coordinates: newProductCoordinates,
                    },
                    active.id.toString(),
                );
            }
        }
    };

    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: { distance: 30 },
    });
    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: { distance: 30 },
    });
    const keyboardSensor = useSensor(KeyboardSensor, {});
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

    return (
        <DndContext
            modifiers={[snapToGrid, restrictToParentElement]}
            sensors={sensors}
            onDragEnd={onDragEndHandler}
            onDragStart={disableScroll}
        >
            <ConfiguratorHeader />
            <ConfiguratorDrawerSizesForm />
            <ConfiguratorDrawer />
            <ConfiguratorProducts />
        </DndContext>
    );
};

Configurator.layout = 'withLastVisitedProducts';

export const getServerSideProps = getServerSidePropsWrapper(({ domainConfig, redisClient, t }) => async (context) => {
    return initServerSideProps({ context, domainConfig, redisClient, t });
});

export default Configurator;
